import * as React from 'react';
import { API_ROUTE, CLIENT_ROUTE, CLIENT_TITLE, paymentTypes } from "./constants";
import { ActionIcon, Button, Flex, Grid } from "@mantine/core";
import { IconPencil, IconPlus, IconSearch, IconTableExport, IconTrash } from "@tabler/icons-react";
import axios from "axios";
import { IUserSubscription, PaymentType } from "./types";
import { Column, useTable } from "react-table";
import { DateRangePicker, DateRangePickerValue } from "@mantine/dates";
import dayjs from "dayjs";
import { useTableStyles } from './useTableStyles';
import styles from "../../components/table/table.module.scss";
import { utils, writeFile } from 'xlsx';
import TableIndicator from "../../components/common/TableStatus";
import { Link, useNavigate } from "react-router-dom";
import { openConfirmModal } from "@mantine/modals";
import { showNotification } from "@mantine/notifications";
import Page from "../../components/layout/Page";
import SearchInput from "../../components/table/SearchInput";
import SongStatItem from "../song/SongStatsItem";
import ScrollControl from "../../components/common/ScrollControl";
import { useTableSort } from "../../hooks";
import { getSortedList } from "../../utils/sort";

interface IFilter {
    startDate: string;
    endDate: string;
}

interface IRecord extends IUserSubscription {
    actions: unknown;
}

interface IUserSubscriptionResponse {
    status: string;
    filter: IFilter;
    result: IRecord[];
    summary: ISummary[];
}

interface ISummary {
    count: number;
    subscriptionId: string;
    title: string;
}

const UserSubscriptionList = () => {

    const { classes } = useTableStyles();
    const navigate = useNavigate();
    const isLoadingRef = React.useRef(false);
    const containerRef = React.useRef<HTMLDivElement>(null);

    const [ records, setRecords ] = React.useState<IRecord[]>([]);
    const [ dateRange, setDateRange ] = React.useState<DateRangePickerValue>([ null, null ]);
    const [ searchValue, setSearchValue ] = React.useState('');
    const [ summary, setSummary ] = React.useState<ISummary[]>([]);
    const { sort, setSort, getSort } = useTableSort();

    React.useEffect(() => {
        void fetchData();
    }, []);

    const data = React.useMemo(() => {
        return getSortedList(sort, records);
    }, [records, sort]);

    const handleSort = (id: string) => {
        const sortObj = getSort(id);
        setSort(sortObj);
    }

    const columns: Column<IRecord>[] = React.useMemo(
        () => [
            {
                Header: 'Хэрэглэгч ID',
                accessor: 'customerId',
            },
            {
                Header: 'Үнэ',
                accessor: 'subscriptionAmount',
            },
            {
                Header: 'Худалдан авалт',
                accessor: 'subscriptionId',
            },
            {
                Header: 'Эхлэх огноо',
                accessor: 'startDate',
                Cell: (table) => {
                    const value = table.row.original.startDate;
                    const date = dayjs(value).format('YYYY-MM-DD HH:ss:mm')
                    return (
                        <div>
                            {date}
                        </div>
                    )
                }
            },
            {
                Header: 'Дуусах огноо',
                accessor: 'endDate',
                Cell: (table) => {
                    const value = table.row.original.endDate;
                    const date = dayjs(value).format('YYYY-MM-DD HH:ss:mm')
                    return (
                        <div>
                            {date}
                        </div>
                    )
                }
            },
            {
                Header: 'Төлбөрийн хэлбэр',
                accessor: 'paidBy',
                Cell(table) {
                    const value = table.row.original.paidBy;
                    return (
                        <div>
                            {paymentTypes[value] ?? '-'}
                        </div>
                    )
                }
            },
            {
                Header: 'Төлөв',
                accessor: 'isActive',
                Cell: ({ value }) => {
                    return <TableIndicator active={value}/>;
                },
                width: 1
            },
            {
                Header: 'Үүссэн огноо',
                accessor: 'createdAt',
                Cell: ({ value }) => {
                    return (
                        <div>
                            {dayjs(value).format('YYYY.MM.DD HH:mm')}
                        </div>
                    );
                }
            },
            {
                Header: '',
                accessor: 'actions',
                Cell: ({ row }) => {
                    return (
                        <Flex
                            justify={'flex-end'}
                            align={'center'}
                        >
                            <ActionIcon color={'blue'} onClick={() => navigate(`${CLIENT_ROUTE}/edit`, { state: row.original._id })}>
                                <IconPencil size={18}/>
                            </ActionIcon>
                            <ActionIcon color={'red'} onClick={() => deleteRecord(row.original._id)}>
                                <IconTrash size={18}/>
                            </ActionIcon>
                        </Flex>
                    )
                }
            },
        ],
        []
    )

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({ columns, data, defaultColumn: {
        defaultCanSort: true
        } });

    const fetchData = async (params?: Record<string, any>) => {
        isLoadingRef.current = true;
        const { data } = await axios.post<IUserSubscriptionResponse>(`${API_ROUTE}/list`, params);
        setRecords(data.result);
        const startDate = dayjs(data.filter.startDate).toDate();
        const endDate = dayjs(data.filter.endDate).toDate();
        setDateRange([startDate, endDate]);
        setSummary(data.summary);
        isLoadingRef.current = false;
    }

    const deleteRecord = (id: string) => {
        openConfirmModal({
            title: 'Устгах',
            children: 'Мэдээлэл устгах уу?',
            labels: {
                confirm: 'Устгах',
                cancel: 'Цуцлах'
            },
            async onConfirm() {
                const { data } = await axios.delete(`${API_ROUTE}/${id}`);
                if (data.statusCode === 200) {
                    navigate(CLIENT_ROUTE);
                    void await fetchData();
                    showNotification({
                        title: 'Мэдэгдэл',
                        message: 'Амжилттай',
                        color: 'green'
                    })
                } else {
                    showNotification({
                        title: 'Мэдэгдэл',
                        message: 'Алдаа гарлаа',
                        color: 'red'
                    })
                }
            }
        });
    }

    const handleExcelExport = () => {

        const excelData: any[] = data.map(item => {
            const object: any = {...item};
            object.startDate = dayjs(object.startDate).format('YYYY.MM.DD HH:mm:ss')
            object.endDate = dayjs(object.endDate).format('YYYY.MM.DD HH:mm:ss')
            object.createdAt = dayjs(object.createdAt).format('YYYY.MM.DD HH:mm:ss')
            delete object.order;
            delete object._id;
            delete object.userId;
            delete object.isActive;
            delete object.createdUserName;
            return object;
        });

        excelData.unshift({
            customerId: 'Хэрэглэгч ID',
            subscriptionId: 'Худалдан авалт',
            startDate: 'Эхлэх огноо',
            endDate: 'Дуусах огноо',
            paidBy: 'Төлбөрийн хэлбэр',
            createdAt: 'Үүссэн огноо',
        })

        const worksheet = utils.json_to_sheet(excelData, {
            header: ['customerId', 'subscriptionId', 'startDate', 'endDate', 'paidBy', 'createdAt'],
            skipHeader: true
        })
        const workbook = utils.book_new();
        utils.book_append_sheet(workbook, worksheet, "Худалдан авалт");

        const [ start, end ] = dateRange.map((date) => dayjs(date).format('YYYY.MM.DD'));
        const fileName = `${start}-${end}`;

        writeFile(workbook, `user-subscription-${fileName}.xlsx`);
    }

    const handleSearchClick = () => {
        const [ startDate, endDate ] = dateRange.map((date) => dayjs(date).format('YYYY.MM.DD'));

        const params = {
            startDate,
            endDate,
            query: searchValue
        }
        void fetchData(params);
    }

    return (
        <Page>
            <Page.Header title={CLIENT_TITLE}>
                <Button
                    component={Link}
                    to={`${CLIENT_ROUTE}/new`}
                    leftIcon={<IconPlus size={18}/>}
                    color={'color-secondary'}
                >
                    Үүсгэх
                </Button>
            </Page.Header>
            <Page.Body>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', width: 550 }}>
                        <div style={{ width: 350 }}>
                            <SearchInput onChange={setSearchValue}/>
                        </div>
                        <DateRangePicker
                            placeholder="Өдөр сонгоно уу"
                            value={dateRange}
                            onChange={setDateRange}
                            style={{ width: 350, marginRight: '1rem' }}
                            clearable={false}
                        />
                        <Button
                            leftIcon={<IconSearch/>}
                            onClick={handleSearchClick}
                        >
                            Хайх...
                        </Button>
                    </div>
                    <Button onClick={handleExcelExport} rightIcon={<IconTableExport/>}>
                        Excel Export
                    </Button>
                </div>
                <Grid gutter="xl" mt={'sm'} mb={'xs'}>
                    {
                        summary.map(summaryItem => {
                            return (
                                <Grid.Col span={'content'} key={summaryItem.subscriptionId}>
                                    <SongStatItem
                                        label={summaryItem.title}
                                        value={summaryItem.count}
                                        color={'rgb(71, 169, 146)'}
                                        isLoading={isLoadingRef.current}
                                    />
                                </Grid.Col>
                            )
                        })
                    }
                </Grid>
                <div style={{ overflowX: 'scroll' }} ref={containerRef}>
                    <table {...getTableProps({ className: styles.table })}>
                        <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps({ className: classes.headerRow })}>
                                <th className={styles.headerCell}>№</th>
                                {headerGroup.headers.map(column => {
                                    let sortText = '';
                                    if (sort?.[column.id] === 1) {
                                        sortText = '🔼'
                                    } else if (sort?.[column.id] === -1) {
                                        sortText = '🔽'
                                    }
                                    return (
                                        <th
                                            {...column.getHeaderProps({ className: styles.headerCell })}
                                            onClick={() => column.defaultCanSort && handleSort(column.id)}
                                        >
                                            {column.render('Header')}
                                            {sortText}
                                        </th>
                                    )
                                })}
                            </tr>
                        ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                        {rows.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps({ className: styles.row })}>
                                    <td className={styles.cell}>{row.index + 1}</td>
                                    {row.cells.map(cell => {
                                        return (
                                            <td
                                                {...cell.getCellProps({ className:  styles.cell })}
                                            >
                                                {cell.render('Cell')}
                                            </td>
                                        )
                                    })}
                                </tr>
                            )
                        })}
                        </tbody>
                    </table>
                </div>
                <ScrollControl
                    vertical={containerRef}
                    horizontal={containerRef}
                />
            </Page.Body>
        </Page>
    )
};

export default UserSubscriptionList;